import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Usager } from '@app/models/consumer';
import { Family } from '@app/models/family';
import { AccueilTreeItem, AlreadyTaggedNiveauCours, LasidoChoiceGroup, LasidoDepartment, LasidoDisciplineTree, LasidoInscription, LasidoInscriptionConfig, LasidoInscriptionWithConfig, LasidoPreInscriptionConfig, LasidoPreInscriptionWithConfig, LasidoPreinscription } from '@app/models/lasido';
import moment from 'moment';
import { map } from 'rxjs/operators';
import { ChildAdapter } from './adapter/child-adapter.service';
import { HTTP_OPTIONS_JSON } from './api-crud.service';

const httpOptions = HTTP_OPTIONS_JSON;

@Injectable({
  providedIn: 'root'
})
export class LasidoService {

  configUrlPreinscription = 'conf/lasido';
  configUrlInscription = 'conf/lasidoInscription';

  constructor(
    private http: HttpClient,
    private childAdapter: ChildAdapter
  ) { }

  getConfigPreinscriptionForAdmin() {
    return this.http.get<{ config: LasidoPreInscriptionConfig, anneesScolaires: [], etablissements: [], departements: [] }>('conf/admin/lasido');
  }

  getConfigPreinscriptionForUser() {
    return this.http.get<LasidoPreInscriptionConfig>(this.configUrlPreinscription);
  }

  saveConfigPreinscription(config: any) {
    return this.http.put(this.configUrlPreinscription, { config }, httpOptions);
  }

  getConfigInscriptionForAdmin() {
    return this.http.get<{ config: LasidoInscriptionConfig, anneesScolaires: [] }>('conf/admin/lasidoInscription');
  }

  getConfigInscriptionForUser() {
    return this.http.get<LasidoInscriptionConfig>(this.configUrlInscription);
  }

  saveConfigInscription(config: any) {
    return this.http.put(this.configUrlInscription, { config }, httpOptions);
  }

  getInscription(f: Family | number, id: number) {
    const idFamily = typeof f === 'object' ? f.id : f;
    return this.http.get<LasidoInscriptionWithConfig>(`familles/${idFamily}/lasido-inscriptions/${id}`);
  }

  getPreinscription(f: Family | number, id: number) {
    const idFamily = typeof f === 'object' ? f.id : f;
    return this.http.get<LasidoPreInscriptionWithConfig>(`familles/${idFamily}/lasido-preinscriptions/${id}`);
  }

  savePreinscription(preinscription: LasidoPreinscription, idFamily: number) {
    return this.http.post<{ errors?: [] }>(`familles/${idFamily}/lasido-preinscriptions`, { preinscription });
  }

  getOptions(family: Family | number) {
    const idFamily = typeof family === 'object' ? family.id : family;

    return this.http.get<{ usagers: Usager[], etablissements: AccueilTreeItem[] }>(`familles/${idFamily}/lasido/options`).pipe(
      map(data => {
        data.usagers.forEach(u => {
          if (u.type === 'enfant') {
            u = this.childAdapter.adapt(u);
          }
        });

        return data;
      })
    );
  }

  getAvailableLevels(idFamily: number, type: string, id: number, annee: number, accueil: number) {
    const url = `familles/${idFamily}/lasido/disciplines-tree/${type}/${id}/${annee}/${accueil}`;

    return this.http.get<{ departements: LasidoDepartment[], alreadyTagged: AlreadyTaggedNiveauCours[] }>(url);
  }

  setGroupAttributes(tree: LasidoDisciplineTree, group: LasidoChoiceGroup, config: LasidoPreInscriptionConfig) {
    const dep = tree.find(d => d.disciplines.find(di => di.id === group.discipline));
    const disc = dep?.disciplines.find(d => d.id === group.discipline);
    const lvl = disc?.niveaux.find(n => n.id === group.niveau);

    if (dep && config.afficherDepartement) {
      group.departmentName = dep.name;
      group.idDepartement = dep.id;
    }

    group.disciplineName = disc?.name;
    group.niveauName = lvl?.name;
    group.hasCours = lvl?.cours && lvl.cours.length > 0;

    if (group.hasCours) {
      group.minCours = Math.min(lvl.cours.length, config.coursMin);
      group.maxCours = Math.min(lvl.cours.length, config.coursMax);
    }
  }

  getLevelData(tree: LasidoDisciplineTree, lvl: number) {
    for (const dep of tree) {
      for (const disc of dep.disciplines) {
        for (const niv of disc.niveaux) {
          if (niv.id === lvl) {
            return { departement: dep.id, discipline: disc.id, niveau: niv.id };
          }
        }
      }
    }
  }

  getCoursByIdNiveau(idNiveau: number) {
    return this.http.get(`cours-by-niveau/${idNiveau}`);
  }


  checkPermRenouvellementInscription(config: LasidoInscriptionConfig): boolean {
    return this.isPeriodOpen(config.startDate, config.endDate);
  }

  checkPermPreinscription(config: LasidoPreInscriptionConfig): boolean {
    // pour les préinscriptions, des dates vides signifient une période sans limite (toujours ouverte)
    return this.isPeriodOpen(config.startDate, config.endDate, true);
  }

  private isPeriodOpen(startDate?: string | Date, endDate?: string | Date, openIfNoDates = false): boolean {
    const start = moment(startDate).isValid() ? moment(startDate) : null;
    const end = moment(endDate).isValid() ? moment(endDate).add(1, 'day') : null;
    const currentDateTime = moment(new Date());

    if ((start && end && currentDateTime.isBetween(start, end))
      || (!end && currentDateTime.isAfter(start))
      || (!start && currentDateTime.isBefore(end))
      || (openIfNoDates && !start && !end)) {
      return true;
    };

    return false;
  }

  saveInscription(inscription: LasidoInscription, idFamily: number) {
    return this.http.post(`familles/${idFamily}/lasido-inscriptions`, { inscription });
  }
}
